import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { authService } from '../../services/authService';

const ChangePasswordForm = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  // Password requirements
  const requirements = [
    { key: 'length', label: t('profile.atLeast8Chars'), test: (pw) => pw.length >= 8 },
    { key: 'uppercase', label: t('profile.oneUppercase'), test: (pw) => /[A-Z]/.test(pw) },
    { key: 'lowercase', label: t('profile.oneLowercase'), test: (pw) => /[a-z]/.test(pw) },
    { key: 'number', label: t('profile.oneNumber'), test: (pw) => /\d/.test(pw) }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = t('validation.required');
    }

    if (!formData.newPassword) {
      newErrors.newPassword = t('validation.required');
    } else {
      // Check all requirements
      const failedReqs = requirements.filter(req => !req.test(formData.newPassword));
      if (failedReqs.length > 0) {
        newErrors.newPassword = t('profile.passwordRequirements');
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.required');
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      await authService.changePassword(formData.currentPassword, formData.newPassword);
      toast.success(t('auth.passwordChanged'));

      // Password change revokes all tokens, so redirect to login
      if (onSuccess) {
        onSuccess();
      } else {
        // Clear local storage and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        navigate('/login', { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.message || t('common.error');
      toast.error(message);
      if (error.response?.status === 401) {
        setErrors({ currentPassword: t('auth.invalidCredentials') });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Current Password */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('auth.currentPassword')}
        </label>
        <div className="relative">
          <input
            type={showCurrentPassword ? 'text' : 'password'}
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            className={`input-field pr-10 ${errors.currentPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder={t('auth.currentPassword')}
          />
          <button
            type="button"
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            {showCurrentPassword ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        {errors.currentPassword && (
          <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
        )}
      </div>

      {/* New Password */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('auth.newPassword')}
        </label>
        <div className="relative">
          <input
            type={showNewPassword ? 'text' : 'password'}
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            className={`input-field pr-10 ${errors.newPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder={t('auth.newPassword')}
          />
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            {showNewPassword ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        {errors.newPassword && (
          <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
        )}

        {/* Password Requirements */}
        <div className="mt-2 space-y-1">
          {requirements.map(req => {
            const passed = req.test(formData.newPassword);
            return (
              <div
                key={req.key}
                className={`flex items-center text-xs ${
                  formData.newPassword
                    ? passed
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-500'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {formData.newPassword ? (
                  passed ? (
                    <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 mr-1.5" />
                  )
                ) : (
                  <span className="h-4 w-4 mr-1.5 rounded-full border border-gray-300 dark:border-gray-600" />
                )}
                {req.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('auth.confirmPassword')}
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={`input-field pr-10 ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder={t('auth.confirmPassword')}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            {showConfirmPassword ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
        )}
        {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
          <p className="mt-1 text-sm text-green-600 flex items-center">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            {t('profile.passwordsMatch')}
          </p>
        )}
      </div>

      {/* Note about logout */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          {t('profile.passwordLogoutWarning')}
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 rtl:space-x-reverse pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          className="btn-primary"
          disabled={loading}
        >
          {loading ? t('common.loading') : t('auth.changePassword')}
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
